import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlayerProfile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [athlete, setAthlete] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyProfile();
  }, []);

  const fetchMyProfile = async () => {
    try {
      const response = await axios.get(`${API}/athletes`);
      if (response.data.length > 0) {
        setAthlete(response.data[0]);
      }
    } catch (error) {
      toast.error('Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      injured: 'bg-red-100 text-red-800 border-red-200',
      inactive: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700'
    };
    return colors[status] || colors.active;
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              My Profile
            </h2>
            <p className="text-gray-600 dark:text-gray-500 mt-1">Your athlete information and performance data</p>
          </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        ) : !athlete ? (
          <Card data-testid="no-profile-message">
            <CardContent className="py-12 text-center">
              <p className="text-gray-500 dark:text-gray-500">Your athlete profile has not been set up yet. Contact an administrator.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Profile Header Card */}
            <Card className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white" data-testid="profile-header">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-3xl mb-2" data-testid="profile-name">{athlete.full_name}</CardTitle>
                    <CardDescription className="text-emerald-50 text-lg">{athlete.position}</CardDescription>
                  </div>
                  <Badge className={`${getStatusBadge(athlete.status)} text-base`} data-testid="profile-status">
                    {athlete.status}
                  </Badge>
                </div>
              </CardHeader>
            </Card>

            {/* Personal Information */}
            <Card data-testid="personal-info-card">
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-500">Date of Birth</p>
                  <p className="font-medium text-lg" data-testid="profile-dob">
                    {new Date(athlete.date_of_birth).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-500">Age</p>
                  <p className="font-medium text-lg" data-testid="profile-age">
                    {new Date().getFullYear() - new Date(athlete.date_of_birth).getFullYear()} years
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-500">Nationality</p>
                  <p className="font-medium text-lg" data-testid="profile-nationality">{athlete.nationality}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-500">Position</p>
                  <p className="font-medium text-lg" data-testid="profile-position">{athlete.position}</p>
                </div>
              </CardContent>
            </Card>

            {/* Status Card */}
            <Card data-testid="status-info-card">
              <CardHeader>
                <CardTitle>Current Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-3">
                  <div className={`h-4 w-4 rounded-full ${
                    athlete.status === 'active' ? 'bg-emerald-500' :
                    athlete.status === 'injured' ? 'bg-red-500' :
                    'bg-gray-50 dark:bg-gray-9000'
                  }`}></div>
                  <span className="text-lg font-medium capitalize">{athlete.status}</span>
                </div>
              </CardContent>
            </Card>

            {/* Quick Links */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Access</CardTitle>
                <CardDescription>View your data and progress</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  className="w-full justify-start bg-pfap-green hover:bg-pfap-green/90 text-white" 
                  onClick={() => navigate(`/athlete/${athlete.id}/360`)}
                  data-testid="view-360-button"
                >
                  🎯 View Full 360° Dashboard
                </Button>
                <Button 
                  className="w-full justify-start" 
                  variant="outline" 
                  onClick={() => navigate('/my-training')}
                >
                  🏃 Training & Attendance
                </Button>
                <Button 
                  className="w-full justify-start" 
                  variant="outline" 
                  onClick={() => navigate('/my-performance')}
                >
                  ⚽ Performance Reports
                </Button>
                <Button 
                  className="w-full justify-start" 
                  variant="outline" 
                  onClick={() => navigate('/my-documents')}
                >
                  📄 Documents
                </Button>
                <Button 
                  className="w-full justify-start" 
                  variant="outline" 
                  onClick={() => navigate('/my-announcements')}
                >
                  📢 Announcements
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
        </div>
      </div>
    </Layout>
  );
};

export default PlayerProfile;
